import type { Workflow } from '@compass/client/types/workflow';
import { ChevronsUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { WorkflowType } from '@/constants/dispositions';
import { cn } from '@/lib/utils';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface WorkflowComboboxProps {
  workflows: Workflow[];
  selectedWorkflowId: string | null;
  onWorkflowChange: (workflowId: string) => void;
  disabled?: boolean;
  /** When true, show the combobox even for a single PQC workflow. Defaults to true. */
  showForPQC?: boolean;
}

/**
 * Searchable workflow dropdown for the header breadcrumb bar.
 * Expects pre-filtered workflows (only those with input configured).
 * Uses Command component for fuzzy search and keyboard navigation.
 * Shows search input only when there are 5 or more workflows.
 */
export function WorkflowCombobox({
  workflows,
  selectedWorkflowId,
  onWorkflowChange,
  disabled = false,
  showForPQC = true,
}: WorkflowComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedWorkflow = useMemo(
    () => workflows.find(w => w.id === selectedWorkflowId),
    [workflows, selectedWorkflowId],
  );

  const hasPQCWorkflow = showForPQC && workflows.some(w => w.workflow_type === WorkflowType.PQC);
  if (workflows.length < 2 && !hasPQCWorkflow) {
    return null;
  }

  const showSearch = workflows.length >= 5;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          data-testid="workflow-switcher"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-6 px-1 border-none inline-flex items-center gap-1',
            'focus:ring-0 focus:ring-offset-0 focus:outline-hidden',
            'text-sm text-foreground',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span>
            {selectedWorkflow?.name || selectedWorkflow?.workflow_type || 'Select Workflow'}
          </span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0 hover:bg-muted p-0.5 rounded-sm" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command>
          {showSearch && <CommandInput placeholder="Search workflows..." className="h-9" />}
          <CommandList>
            <CommandEmpty>No workflows found</CommandEmpty>
            <CommandGroup>
              {workflows.map(workflow => (
                <CommandItem
                  key={workflow.id}
                  value={workflow.name || workflow.workflow_type || workflow.id}
                  onSelect={() => {
                    if (workflow.id) {
                      onWorkflowChange(workflow.id);
                      setOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{workflow.name || workflow.workflow_type || 'Workflow'}</span>
                    {workflow.id === selectedWorkflowId && (
                      <span className="text-xs text-muted-foreground">✓</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
